import React, { useState } from 'react';

import { metaApis } from '@apis/next/metaApis';
import { Button } from '@components/Button';
import { DevLayout } from '@components/dev/DevLayout';
import { TextInput } from '@components/NextForms/TextInput';
import { useUser } from '@hooks/queries/useUser';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMemoVersion } from '@hooks/useMemoVersion';
import { Meta } from '@models/next/Meta';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { AuthManager } from '@store/AuthManager';
import { getConfig } from '@utils/config';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { z } from 'zod';

const IsMaintenanceForm: React.FC<{ meta: Meta }> = ({ meta }) => {
  const form = useForm(
    z.object({
      is_maintenance: z.boolean(),
    }),
    {
      initialOutput: meta,
    },
  );

  const patchMeta = metaApis.usePatch('/dashboard/meta');

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [patchMeta] });

  const [message, setMessage] = useState<string>();

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          await patchMeta.mutateAsync(values);
          if (values.is_maintenance) {
            setMessage('WARNING: entered maintenance mode!');
          } else {
            setMessage('Maintenance mode is over!');
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}

      <TextInput
        field={form.root.fields.is_maintenance}
        inputOptions={{
          type: 'choose',
          options: [
            {
              value: true,
              label: 'true',
            },
            {
              value: false,
              label: 'false',
            },
          ],
        }}
        onSelect={() =>
          form.element?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
        }
      />

      {message && <div style={{ color: 'red' }}>{message}</div>}
    </form>
  );
};

const LocalStorageFlagForm: React.FC<{ flag: string }> = ({ flag }) => {
  const form = useForm(
    z.object({
      flag: z.boolean(),
    }),
    {
      initialOutput: {
        flag: !!localStorage.getItem(flag),
      },
    },
  );

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          if (values.flag) {
            localStorage.setItem(flag, 'true');
          } else {
            localStorage.removeItem(flag);
          }
        },
      })}
    >
      <TextInput
        field={form.root.fields.flag}
        inputOptions={{
          type: 'choose',
          options: [
            {
              value: true,
              label: 'true',
            },
            {
              value: false,
              label: 'false',
            },
          ],
        }}
        onSelect={() =>
          form.element?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
        }
      />
    </form>
  );
};

export const About: React.FC = observer(() => {
  const user = useUser(undefined, {
    enabled: AuthManager.shared.isLoggedIn,
  });

  const meta = metaApis.useGet('/meta', undefined, {
    refetchInterval: 5000,
  });

  const pdfMeta = metaApis.useGet('/pdf/meta', undefined, {
    refetchInterval: 5000,
  });

  const queries = [meta, pdfMeta];

  const chatURL = `${getConfig().d2dFrontendZendeskUrl}?key=${AuthManager.shared.key}`;

  const [metaGitCommitVersion, pdfMetaVersion] = [
    useMemoVersion(meta.data?.git_commit),
    useMemoVersion(pdfMeta.data?.GIT_COMMIT),
  ];

  return (
    <DevLayout>
      <div style={{ padding: 10 }}>
        <table>
          <tr>
            <td>User</td>
            <td>
              {user.isSuccess && (
                <>
                  {user.data?.email}:{user.data?.id}
                </>
              )}

              {user.status === 'loading' && user.fetchStatus === 'idle' && <>not logged in</>}
            </td>
          </tr>

          <tr>
            <td>Chat URL</td>

            <td>
              <a target="_blank" href={chatURL}>
                {chatURL}
              </a>
            </td>
          </tr>

          <tr>
            <td>Client Commit</td>
            <td>
              <a
                style={{ color: 'green' }}
                href={`https://github.com/MonoidDev/d2d-frontend/commit/${GIT_HASH}`}
              >
                {process.env.VITE_APP_STAGE}:{GIT_HASH}
              </a>
            </td>
          </tr>

          <tr>
            <td>Client Build Time</td>

            <td>{dayjs(BUILD_TIME).toDate().toLocaleString()}</td>
          </tr>

          <tr>
            <td>Server Commit</td>
            <td>
              <a
                style={{ color: 'green' }}
                href={`https://github.com/MonoidDev/d2d-backend/commit/${meta.data?.git_commit}`}
              >
                {meta.data?.git_commit ?? 'Loading...'}({metaGitCommitVersion})
              </a>
            </td>
          </tr>

          <tr>
            <td>Server Commit Last Fetched</td>
            <td>{new Date(meta.dataUpdatedAt).toLocaleString()}</td>
          </tr>

          <tr>
            <td>Pdf Server Commit</td>
            <td>
              <a style={{ color: 'green' }} href={pdfMeta.data?.GITHUB_COMMIT}>
                {pdfMeta.data?.GIT_COMMIT ?? 'Loading...'}({pdfMetaVersion})
              </a>
            </td>
          </tr>

          <tr>
            <td>Pdf Server Mode</td>
            <td>{pdfMeta.data?.MODE ?? 'Loading...'}</td>
          </tr>

          <tr>
            <td>Pdf Meta Last Fetched</td>
            <td>{new Date(pdfMeta.dataUpdatedAt).toLocaleString()}</td>
          </tr>

          <tr>
            <td>Is Maintenance</td>
            <td>{meta.data && <IsMaintenanceForm meta={meta.data} />}</td>
          </tr>

          <tr>
            <td>Ignore Maintenance Redirection</td>
            <td>
              <LocalStorageFlagForm flag="IGNORE_MAINTENANCE_REDIRECT" />
            </td>
          </tr>

          <tr>
            <td>Ignore API Error Dialog</td>
            <td>
              <LocalStorageFlagForm flag="INGORE_API_ERROR_DIALOG" />
            </td>
          </tr>
        </table>

        <Button
          color="primary"
          text="Refetch"
          onClick={() => queries.forEach((q) => q.refetch())}
        />
      </div>
    </DevLayout>
  );
});
